import { Directive, ElementRef, Input, EventEmitter, Output, NgZone, OnDestroy, Renderer2 } from '@angular/core';
import { GridComponent } from '@progress/kendo-angular-grid';

export interface GridRowEventListenerOptions {
  zone?: NgZone;
  hover?: boolean;
  mouseDown?: boolean;
  rowData?: any;
}

export interface RowMouseDownData {
  index: number;
  clickedByCheckbox: boolean;
  originalEvent?: MouseEvent;
}

@Directive({
  standalone: false,
  selector: '[tiGridRowEventListener]',
})
export class GridRowEventListenerDirective implements OnDestroy {
  @Input('tiGridRowEventListener') options: GridRowEventListenerOptions;
  @Output() rowMouseDown = new EventEmitter<RowMouseDownData>();
  @Output() rowHover = new EventEmitter<any>();

  currentRow: any = null;

  private listeners: { row: HTMLElement; listener: any; eventName: string }[] = [];
  private rowListeners = [];
  private dragNDropWrappers: HTMLElement[] = [];

  constructor(
    private grid: ElementRef<HTMLElement>,
    private component: GridComponent,
    private renderer: Renderer2,
  ) {}

  setHoverStateListener(options?: GridRowEventListenerOptions) {
    this.options = options || this.options;
    this.removeHoverStateListener();

    if (this.grid.nativeElement.querySelector(`.k-detail-row`)) {
      // !this.grid.nativeElement.querySelector(`.k-grid-content-locked`) ||
      return;
    }

    if (this.grid.nativeElement.querySelector(`.k-grid .k-grid-container tr.ti-card-view-tr`)) {
      this.options.zone &&
        this.options.zone.runOutsideAngular(() => {
          Array.from(this.grid.nativeElement.querySelectorAll(`.k-grid .k-grid-container tr.ti-card-view-tr`)).forEach(
            (row: HTMLElement) => {
              if (this.options.hover) {
                this.addEventListener(row, 'mouseenter', () => this.onGridRowMouseEnter(row, this.grid, true));
                this.addEventListener(row, 'mouseleave', () => this.onGridRowMouseLeave(row, this.grid, true));
              }
              if (this.options.mouseDown) {
                this.addEventListener(row, 'mousedown', (e) => this.onGridRowMouseDown(row, this.grid, e));
              }
            },
          );
        });
      return;
    }

    this.options.zone &&
      this.options.zone.runOutsideAngular(() => {
        Array.from(this.grid.nativeElement.querySelectorAll('.k-grid .k-grid-container tr')).forEach(
          (row: HTMLElement) => {
            if (this.options.hover) {
              this.addEventListener(row, 'mouseenter', () => this.onGridRowMouseEnter(row, this.grid));
              this.addEventListener(row, 'mouseleave', () => this.onGridRowMouseLeave(row, this.grid));
            }
            if (this.options.mouseDown) {
              this.addEventListener(row, 'mousedown', (e) => this.onGridRowMouseDown(row, this.grid, e));
            }
          },
        );
      });
  }

  removeHoverOnItemByIndex(index: number) {
    if (index === undefined && this.grid.nativeElement.querySelector(`.k-detail-row`)) {
      return;
    }

    const nodes = this.grid.nativeElement.querySelectorAll(`tr[data-kendo-grid-item-index="${index}"]`);
    Array.from(nodes).forEach((n) => n.classList.remove('k-state-hover'));
  }

  removeHoverStateListener() {
    if (!this.listeners.length || this.grid.nativeElement.querySelector(`.k-detail-row`)) {
      //  || !this.grid.nativeElement.querySelector(`.k-grid-content-locked`)
      return;
    }

    this.options.zone.runOutsideAngular(() => {
      this.listeners.forEach((item) => item.row.removeEventListener(item.eventName, item.listener));
      this.listeners = [];
    });
  }

  ngOnDestroy() {
    this.removeHoverStateListener();
    this.removeDragEnterRowListeners();
  }

  private addEventListener(row: HTMLElement, eventName: string, handler) {
    this.listeners.push({
      row: row,
      eventName: eventName,
      listener: handler,
    });

    row.addEventListener(eventName, handler);
  }

  private onGridRowMouseEnter(row, grid, emitHover?: boolean) {
    // console.log('HOVER row', row);
    // console.log('HOVER grid', grid);

    if (row.classList.contains('k-grid-norecords')) {
      return;
    }

    if (!row.closest('.k-grid-content') && !row.closest('.k-grid-content-locked')) {
      return;
    }

    this.options.zone.runOutsideAngular(() => {
      const index = row.getAttribute('data-kendo-grid-item-index');

      const el = this.findContentLockedRow(grid.nativeElement, index);
      el && el.classList.add('k-state-hover');

      const mainGrid = grid.nativeElement.querySelector(`.k-grid-content`);

      const contentEl = mainGrid ? mainGrid.querySelector(`tr[data-kendo-grid-item-index="${index}"]`) : null;
      contentEl && contentEl.classList.add('k-state-hover');
      if (emitHover) {
        if (!this.currentRow) {
          this.currentRow = row;
          this.rowHover.emit(index);
        } else if (this.currentRow !== row) {
          this.currentRow = row;
          this.rowHover.emit(index);
        }
      }
    });
  }

  private onGridRowMouseLeave(row, grid, emitHover?: boolean) {
    //console.log('leave', row);
    if (row.classList.contains('k-grid-norecords')) {
      return;
    }

    if (!row.closest('.k-grid-content') && !row.closest('.k-grid-content-locked')) {
      return;
    }

    this.options.zone.runOutsideAngular(() => {
      const index = row.getAttribute('data-kendo-grid-item-index');

      const el = this.findContentLockedRow(grid.nativeElement, index);
      el && el.classList.remove('k-state-hover');

      const mainGrid = grid.nativeElement.querySelector(`.k-grid-content`);

      const contentEl = mainGrid ? mainGrid.querySelector(`tr[data-kendo-grid-item-index="${index}"]`) : null;
      contentEl && contentEl.classList.remove('k-state-hover');
      if (emitHover) {
        this.currentRow = null;
        this.rowHover.emit(null);
      }
    });
  }

  private onGridRowMouseDown(row, grid, event) {
    this.rowMouseDown.emit({
      index: +row.getAttribute('data-kendo-grid-item-index'),
      clickedByCheckbox: event.target.className
        .split(' ')
        .some((item) => item === 'k-checkbox' || item === 'k-checkbox-label'),
      originalEvent: event,
    });
  }

  setRowsEventListener(
    targetTableRows: any[],
    anotherTableRows: any[],
    items: { id: string }[],
    hiddenBorderPosition: 'right' | 'left',
  ): void {
    Array.from(targetTableRows).forEach((gridRow: HTMLElement, index: number, rows: HTMLElement[]) => {
      const childNodes = Array.from(gridRow['childNodes']);
      if (
        (!gridRow.previousElementSibling || !gridRow.previousElementSibling.classList.contains('row-wrapper')) &&
        !gridRow.classList.contains('k-disabled')
      ) {
        const wrapper = this.renderer.createElement('div');
        const wrapperListener = this.renderer.listen(wrapper, 'dragleave', (e: DragEvent) =>
          this.setRowListenerBehavior(e, anotherTableRows, items, false),
        );
        this.rowListeners.push(wrapperListener);
        this.renderer.addClass(wrapper, 'row-wrapper');
        this.renderer.addClass(wrapper, `row-wrapper__${hiddenBorderPosition}`);

        this.renderer.insertBefore(gridRow.parentNode, wrapper, gridRow);

        this.dragNDropWrappers.push(wrapper);
      }

      Array.from(childNodes.filter((node) => node['tagName'] === 'TD')).forEach((td) => {
        const tdListener = this.renderer.listen(td, 'dragenter', (e: DragEvent) =>
          this.setRowListenerBehavior(e, anotherTableRows, items, true),
        );
        this.rowListeners.push(tdListener);
      });
    });
  }

  private setRowListenerBehavior(
    e: DragEvent,
    anotherTableRows: HTMLElement[],
    items: { id: string }[],
    showHighlight: boolean,
  ): void {
    // if (e.dataTransfer.types.some(item => item === 'Files')) {
    //   e.preventDefault();
    //   return;
    // }

    if (showHighlight) {
      // dragenter
      this.dragNDropWrappers.forEach((wrapper) => this.renderer.setStyle(wrapper, 'display', 'none'));
    }

    const target: HTMLElement = <HTMLElement>e.target;
    const row: HTMLElement =
      target.classList && target.classList.contains('row-wrapper')
        ? target.nextSibling
        : this.renderer.parentNode(e.target);

    const rowId = row.getAttribute('rowId');

    if (row.classList.contains('k-disabled') || !rowId) {
      return;
    }

    let rowIndex: number;
    items.forEach((item, index) => {
      if (item.id === rowId) {
        rowIndex = index;
      }
    });

    this.renderer.setStyle(row.previousElementSibling, 'display', showHighlight ? 'block' : 'none');

    if (anotherTableRows[rowIndex]) {
      this.renderer.setStyle(
        anotherTableRows[rowIndex].previousElementSibling,
        'display',
        showHighlight ? 'block' : 'none',
      );
    }
  }

  removeDragEnterRowListeners() {
    this.rowListeners.forEach((listener) => listener());
    this.rowListeners = [];
    this.dragNDropWrappers.forEach((wrapper) => wrapper.remove());
    this.dragNDropWrappers = [];
  }

  private findContentLockedRow(grid: HTMLElement, index: string | number): HTMLElement {
    if (!!grid.querySelector('.k-detail-row') || !grid.querySelector('.k-grid-content-locked')) {
      return null;
    }

    return grid.querySelector(`.k-grid-content-locked tr[data-kendo-grid-item-index="${index}"]`);
  }
}
